import React, { useState, useEffect } from 'react';
import { Plus, Megaphone, Pin, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { useSocietyFeatures } from '../../context/SocietyFeaturesContext';
import { notices as api, type Notice, type NoticeReceiptSummary, type ApiError } from '../../services/api';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};

const INIT = { title: '', body: '', pinned: false, expiresAt: '', notifyWhatsapp: false, emergency: false };

const NoticesPage: React.FC = () => {
  const { user } = useAuth();
  const { features } = useSocietyFeatures();
  const societyId = user?.societyId ?? null;
  const isResident = user?.role === 'RESIDENT';
  const canManage =
    user?.role === 'SOCIETY_ADMIN' ||
    user?.role === 'STAFF' ||
    (isResident && features.noticeboardEnabled);
  const [items, setItems] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(INIT);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [marking, setMarking] = useState<string | null>(null);
  const [receipts, setReceipts] = useState<Record<string, NoticeReceiptSummary>>({});

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  useEffect(() => {
    if (!societyId || !canManage) return;
    const emergencyIds = items.filter(n => n.emergency).map(n => n.id);
    emergencyIds.forEach(id => {
      if (receipts[id]) return;
      api.getReceipts(societyId, id).then(r => setReceipts(p => ({ ...p, [id]: r }))).catch(() => {});
    });
  }, [societyId, items, canManage]);

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    setForm(p => ({ ...p, [target.name]: target.type === 'checkbox' ? target.checked : target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError('');
    try {
      const created = await api.create(societyId, {
        title: form.title, body: form.body, pinned: form.pinned,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : undefined,
        notifyWhatsapp: form.notifyWhatsapp,
        emergency: form.emergency,
      });
      setItems(p => [created, ...p]);
      setShowForm(false); setForm(INIT);
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to post notice');
    } finally { setSaving(false); }
  };

  const requestDeleteNotice = (id: string) => { setConfirmId(id); setDeleteError(''); };

  const confirmDelete = async () => {
    if (!societyId || !confirmId) return;
    setDeletingId(confirmId);
    try {
      await api.delete(societyId, confirmId);
      setItems(p => p.filter(n => n.id !== confirmId));
      setConfirmId(null);
    } catch { setDeleteError('Failed to delete notice. Please try again.'); }
    finally { setDeletingId(null); }
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

  const handleMarkRead = async (id: string) => {
    if (!societyId) return;
    setMarking(id);
    try {
      await api.markRead(societyId, id);
      setReadIds(p => new Set(p).add(id));
    } catch { /* ignore */ }
    finally { setMarking(null); }
  };

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Noticeboard
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>{items.length} notices · {items.filter(n => n.pinned).length} pinned</p>
        </div>
        {canManage && (
          <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> Post Notice
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="nx-card" style={{ padding: 60, textAlign: 'center' }}>
          <Megaphone size={32} style={{ color: 'var(--nx-text-muted)', display: 'block', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 14, color: 'var(--nx-text-muted)' }}>No notices posted yet.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map(n => (
            <div key={n.id} className="nx-card" style={{ padding: '18px 22px', borderLeft: n.emergency ? '3px solid #EF4444' : n.pinned ? '3px solid var(--nx-blue)' : '3px solid transparent', background: n.emergency ? 'rgba(239,68,68,0.04)' : undefined, position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    {n.emergency && <AlertTriangle size={12} style={{ color: '#EF4444', flexShrink: 0 }} />}
                    {n.pinned && <Pin size={12} style={{ color: 'var(--nx-blue)', flexShrink: 0 }} />}
                    <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
                      {n.title}
                    </h3>
                    {n.emergency && (
                      <span style={{ padding: '1px 6px', borderRadius: 99, fontSize: 10, fontWeight: 600, background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>
                        Emergency
                      </span>
                    )}
                    {n.pinned && (
                      <span style={{ padding: '1px 6px', borderRadius: 99, fontSize: 10, fontWeight: 600, background: 'rgba(26,108,246,0.1)', color: '#1A6CF6' }}>
                        Pinned
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--nx-text-secondary)', lineHeight: 1.55, marginBottom: 8 }}>{n.body}</p>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>Posted {fmt(n.createdAt)}</span>
                    {n.expiresAt && <span>Expires {fmt(n.expiresAt)}</span>}
                    {n.emergency && canManage && receipts[n.id] && (
                      <span>{receipts[n.id].readCount}/{receipts[n.id].totalRecipients} read</span>
                    )}
                    {n.emergency && isResident && (
                      readIds.has(n.id) ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#10B981' }}>
                          <CheckCircle2 size={12} /> Read
                        </span>
                      ) : (
                        <button
                          onClick={() => handleMarkRead(n.id)}
                          disabled={marking === n.id}
                          style={{ fontSize: 11, color: 'var(--nx-blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                        >
                          {marking === n.id ? 'Marking…' : 'Mark as read'}
                        </button>
                      )
                    )}
                  </div>
                </div>
                {canManage && (
                  <button
                    onClick={() => requestDeleteNotice(n.id)}
                    disabled={deletingId === n.id}
                    style={{ width: 28, height: 28, border: 'none', background: 'rgba(239,68,68,0.08)', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EF4444', flexShrink: 0 }}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmId !== null}
        variant="danger"
        title="Delete Notice"
        message="Are you sure you want to delete this notice? This action cannot be undone."
        confirmLabel="Delete Notice"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setConfirmId(null); setDeleteError(''); }}
      />

      {showForm && canManage && (
        <Modal
          title="Post Notice" submitLabel="Post Notice" error={formError} isLoading={saving}
          onClose={() => { setShowForm(false); setForm(INIT); setFormError(''); }}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Title *</label>
            <input name="title" value={form.title} onChange={change} required placeholder="Annual General Meeting — Dec 15" style={inp} />
          </div>
          <div>
            <label style={lbl}>Body *</label>
            <textarea
              name="body" value={form.body}
              onChange={change} required placeholder="Notice details, instructions, or announcements…"
              style={{ ...inp, minHeight: 100, resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={lbl}>Expires At</label>
            <input type="datetime-local" name="expiresAt" value={form.expiresAt} onChange={change} style={inp} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--nx-text-secondary)' }}>
            <input
              type="checkbox" name="pinned"
              checked={form.pinned}
              onChange={change}
              style={{ width: 15, height: 15, cursor: 'pointer' }}
            />
            Pin this notice to the top
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--nx-text-secondary)' }}>
            <input
              type="checkbox" name="notifyWhatsapp"
              checked={form.notifyWhatsapp}
              onChange={change}
              style={{ width: 15, height: 15, cursor: 'pointer' }}
            />
            Send via WhatsApp to all residents
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--nx-text-secondary)' }}>
            <input
              type="checkbox" name="emergency"
              checked={form.emergency}
              onChange={change}
              style={{ width: 15, height: 15, cursor: 'pointer' }}
            />
            Emergency broadcast (forces WhatsApp + email to all residents, tracks read receipts)
          </label>
        </Modal>
      )}
    </PageLayout>
  );
};

export default NoticesPage;
